import { useCallback, useEffect, useState } from 'react';

import {
  releaseNewsView,
  versionToAcknowledge,
  type ReleaseNewsView,
} from '@/components/release-news/release-news-model';
import { useAuth } from '@/hooks/use-auth';
import { apiClient } from '@/lib/api';
import { withCleanup } from '@/lib/with-cleanup';
import type { ReleaseNewsResponse } from '@contract-analyzer/api-client';

export interface UseReleaseNewsReturn {
  /** What the dialog shows, or `null` while it is closed. */
  readonly view: ReleaseNewsView | null;
  /** The lookup finished (or failed), so other prompts can decide whether to open. */
  readonly settled: boolean;
  readonly data: ReleaseNewsResponse | null;
  /** Closes the dialog; closing it over unseen releases marks them seen. */
  readonly close: () => void;
  /** Opens every release, newest first. */
  readonly showAll: () => void;
}

export function useReleaseNews(): UseReleaseNewsReturn {
  const { user } = useAuth();
  const [data, setData] = useState<ReleaseNewsResponse | null>(null);
  const [acknowledgedVersion, setAcknowledgedVersion] = useState<string | null>(null);
  const [requestedView, setRequestedView] = useState<ReleaseNewsView | null>(null);
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    if (!user) return;

    let cancelled = false;

    async function fetchReleaseNews() {
      await withCleanup(async () => {
        try {
          const response = await apiClient.getCurrentReleaseNews();
          if (!cancelled) {
            setData(response);
          }
        } catch {
          // Non-critical — silently ignore
        }
      }, () => {
        if (!cancelled) {
          setSettled(true);
        }
      });
    }

    void fetchReleaseNews();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const close = useCallback(() => {
    setRequestedView(null);
    const version = versionToAcknowledge(data, acknowledgedVersion);
    if (version !== null) {
      setAcknowledgedVersion(version);
      void apiClient.acknowledgeReleaseNews(version);
    }
  }, [data, acknowledgedVersion]);

  const showAll = useCallback(() => {
    setRequestedView('all');
  }, []);

  return {
    view: releaseNewsView(data, acknowledgedVersion, requestedView),
    settled,
    data,
    close,
    showAll,
  };
}
